<template>
  <div ref="waterjetBedContainer" class="waterjet-bed">
    <canvas ref="waterjetCanvas" @click="canvasClicked">

    </canvas>
  </div>
</template>


<style scoped>
.waterjet-bed {
  width: 100%;
  flex-grow: 1.0;
  overflow: hidden;
}
.waterjet-bed canvas {
  display: block;
}
</style>


<script>
import MathsUtils from '@/utils/mathsUtils'
import ProductionPanel from '@/apps/glass-production-station/src/classes/productionPanel'

import { AlignmentTypes } from '@/store/mathTypes'

export default {
  name: 'WaterjetBed',
  props: {
    bedBayList: Array,
    bedConfig: Object,
    cutoutOptionsAvailable: Boolean,
    zoomScale: {
      type: Number,
      default: 1.0
    }
  },
  data() {
    return {
      scale: 1.0,
      rulerSize: 20,
      negativeSpaceSize: 30,      // Area below origin (0,0) that hardware can account for for lead in/outs
      gridSize: 100,
      negativeSpaceColour: '#FF404020',
      rulerBackground: '#1E1E1E',
      rulerLabelColor: '#A0A0A0',
      backgroundColour: '#141414',
      borderColour: '#505050',
      axisColour: '#FFFFFF',
      gridColour: '#202020',
      positionColour: '#F0F000',
      originGuideColour: '#00FFFF',
      bedRegion: {
        left: 0,
        top: 0,
        right: 0,
        bottom: 0
      },
      pan: {
        grabbed: false,
        panned: false,
        startPointX: 0,
        startPointY: 0,
        grabPointX: 0,
        grabPointY: 0
      },
      originOffsetX: 0,
      originOffsetY: 0
    }
  },
  computed: {
  },
  watch: {
    zoomScale: function() {
      this.render()
    }
  },
  mounted() {
    this.originOffsetX = this.negativeSpaceSize * this.scale
    this.originOffsetY = this.negativeSpaceSize * this.scale

    // Render to calculate scale & stuff
    this.render()
  },
  beforeDestroy() {
  },
  updated() {
    this.$nextTick(() => {
      this.render()
    })
  },
  methods: {
    onMouseDown(ev) {
      this.pan.grabbed = true
      this.pan.panned = false
      this.pan.grabPointX = ev.offsetX
      this.pan.grabPointY = ev.offsetY
      this.pan.startPointX = ev.offsetX
      this.pan.startPointY = ev.offsetY

      ev.preventDefault()
    },
    onMouseMove(ev) {
      let grabMinDist = 20

      if(!ev.buttons)
        this.pan.grabbed = false

      if(this.pan.grabbed) {
        let directionX = this.bedConfig.originHorizontal == AlignmentTypes.left ? 1 : -1
        let directionY = this.bedConfig.originVertical == AlignmentTypes.top ? 1 : -1

        this.originOffsetX += (ev.offsetX - this.pan.grabPointX) * directionX
        this.originOffsetY += (ev.offsetY - this.pan.grabPointY) * directionY
        
        this.originOffsetX = Math.min(this.originOffsetX, this.negativeSpaceSize * this.scale * this.zoomScale)
        this.originOffsetY = Math.min(this.originOffsetY, this.negativeSpaceSize * this.scale * this.zoomScale)

        this.pan.grabPointX = ev.offsetX
        this.pan.grabPointY = ev.offsetY
        
        if(Math.abs(this.pan.grabPointX - this.pan.startPointX) < grabMinDist && Math.abs(this.pan.grabPointY - this.pan.startPointY) < grabMinDist) {
          this.pan.panned = true
        }

        this.render()
      }      
    },
    onMouseUp(ev) {
      this.pan.grabbed = false
    },
    resetPan() {
      this.originOffsetX = this.negativeSpaceSize * this.scale * this.zoomScale
      this.originOffsetY = this.negativeSpaceSize * this.scale * this.zoomScale
      this.render()
    },
    canvasClicked(ev) {
      if(this.pan.panned)
        return

      let left = ev.offsetX
      let top = ev.offsetY

      // Convert to waterjet coords
      let scale = Math.max(this.scale * this.zoomScale, 0.001)
      var positionX = (left - this.bedRegion.left - this.originOffsetX) / scale
      var positionY = (top - this.bedRegion.top + this.originOffsetY) / scale
      let clickRadius = 15 / scale

      if(this.bedConfig.originVertical == AlignmentTypes.bottom)
        positionY = (this.bedRegion.bottom - this.bedRegion.top) / scale - positionY
      if(this.bedConfig.originHorizontal == AlignmentTypes.right)
        positionY = (this.bedRegion.right - this.bedRegion.left) / scale - positionX

      this.bedBayList.forEach(bay => {
        if(bay.panelObj) {
          // Check what part of the panel was clicked on
          let objectInfo = bay.panelObj.getClosestWaterjetObjectToPosition(positionX, positionY, clickRadius, this.bedConfig)
          if(objectInfo) {
            this.$nextTick(() => {
              this.$emit('panelClicked', {
                event: ev,
                bayIndex: bay.index,
                panel: bay.panelObj,
                panelClickInfo: objectInfo
              })
            })

            ev.stopPropagation()
          }
        }
      })
    },
    render() {
      let canvas = this.$refs.waterjetCanvas
      let ctx = canvas.getContext('2d')
      let ctr = this.$refs.waterjetBedContainer

      canvas.width = ctr.clientWidth
      canvas.height = ctr.clientHeight

      this.bedRegion.left = this.bedConfig.originHorizontal == AlignmentTypes.left ? this.rulerSize : 0
      this.bedRegion.top = this.bedConfig.originVertical == AlignmentTypes.top ? this.rulerSize : 0
      this.bedRegion.right = this.bedConfig.originHorizontal == AlignmentTypes.right ? canvas.width - this.rulerSize : canvas.width
      this.bedRegion.bottom = this.bedConfig.originVertical == AlignmentTypes.bottom ? canvas.height - this.rulerSize : canvas.height

      let scaledBed = MathsUtils.aspectFitResize(this.bedConfig.width, this.bedConfig.height, canvas.width, canvas.height)
      if(this.bedConfig.width > 0)
        this.scale = scaledBed.width / this.bedConfig.width
      
      this.originOffsetX = Math.min(this.originOffsetX, this.negativeSpaceSize * this.scale * this.zoomScale)
      this.originOffsetY = Math.min(this.originOffsetY, this.negativeSpaceSize * this.scale * this.zoomScale)

      ctx.fillStyle = this.backgroundColour
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      ctx.save()

      // Draw the negative space area
      // This is the 4mm region before the 0,0 origin the waterjet bed allows the head to position to
      this.drawNegativeSpace(ctx, canvas.width, canvas.height)

      // Grid
      this.drawGrid(ctx, canvas.width, canvas.height)

      // Draw the origin guide
      // L shaped piece of wood that acts as a guide against the panel for the origin point
      // Drawn at each bay position
      // Note: Currently assumes guides for each position are the same size
      this.drawOriginGuides(ctx, canvas.width, canvas.height)     

      // Positions
      this.drawPositions(ctx, canvas.width, canvas.height)

      // Panels
      this.bedBayList.forEach(bay => {
        if(bay.panelObj)
          this.drawProductionPanel(ctx, bay, canvas.width, canvas.height)
      })

      // Rulers
      this.drawRulerBackgrounds(ctx, canvas.width, canvas.height)
      this.drawRulers(ctx, canvas.width, canvas.height)
      

      ctx.restore()

      // Outline
      ctx.lineWidth = 1
      ctx.strokeStyle = this.borderColour
      ctx.strokeRect(0.5, 0.5, canvas.width-0.5, canvas.height-0.5)


      this.$emit('updateScale', this.scale)
    },


    drawRulerBackgrounds(ctx, width, height) {
      let isLeftRuler = this.bedConfig.originHorizontal == AlignmentTypes.left
      let isTopRuler = this.bedConfig.originVertical == AlignmentTypes.top

      // Horizontal
      ctx.fillStyle = this.rulerBackground
      ctx.fillRect(0, isTopRuler ? 0 : height-this.rulerSize, width, this.rulerSize)

      // Vertical
      ctx.fillStyle = this.rulerBackground
      ctx.fillRect(isLeftRuler ? 0 : width-this.rulerSize, 0, this.rulerSize, height)

      // Outlines
      ctx.strokeStyle = this.borderColour
      ctx.beginPath()
      ctx.moveTo(this.bedRegion.left + 0.5, (isTopRuler ? this.bedRegion.top : this.bedRegion.bottom) + 0.5)
      ctx.lineTo(this.bedRegion.right, (isTopRuler ? this.bedRegion.top : this.bedRegion.bottom) + 0.5)      

      ctx.moveTo((isLeftRuler ? this.bedRegion.left : this.bedRegion.right) + 0.5, this.bedRegion.top)
      ctx.lineTo((isLeftRuler ? this.bedRegion.left : this.bedRegion.right) + 0.5, this.bedRegion.bottom)
      ctx.stroke()
    },


    drawRulers(ctx, width, height) {
      let tickDist = this.gridSize * this.scale * this.zoomScale
      let smallTickLength = 4
      let labelTickLength = 10
      let labelTickCount = 10

      let isLeftRuler = this.bedConfig.originHorizontal == AlignmentTypes.left
      let isTopRuler = this.bedConfig.originVertical == AlignmentTypes.top

      let horizontalRulerDistance = width + Math.abs(this.originOffsetX)
      let verticalRulerDistance = height + Math.abs(this.originOffsetY)

      var t = 0
      var origin = 0

      ctx.font = '11px Arial'
      ctx.textAlign = 'left'
      ctx.strokeStyle = this.borderColour
      ctx.beginPath()

      // Horizontal - Ticks
      var tickIndex = 0
      origin = this.originOffsetX
      for(t=0; t<horizontalRulerDistance; t += tickDist, tickIndex++) {
        let tickLength = smallTickLength
        if(tickIndex % labelTickCount == 0)
          tickLength = labelTickLength
        
        if(tickIndex == 0)
          tickLength = labelTickLength

        let tickX = isLeftRuler ? Math.floor(this.bedRegion.left + t + origin) : Math.floor(this.bedRegion.right - t - origin)
        let topSpacer = 1

        if(origin + t >= 0 && origin + t <= horizontalRulerDistance) {
          if(isTopRuler) {
            ctx.moveTo(tickX+0.5, this.bedRegion.top - tickLength + topSpacer + 0.5)
            ctx.lineTo(tickX+0.5, this.bedRegion.top + topSpacer + 0.5)
          } else {
            ctx.moveTo(tickX+0.5, this.bedRegion.bottom + topSpacer + 0.5)
            ctx.lineTo(tickX+0.5, this.bedRegion.bottom + tickLength + topSpacer + 0.5)
          }
        }
      }

      // Vertical - Ticks
      tickIndex = 0
      origin = this.originOffsetY
      for(t=0; t<verticalRulerDistance; t += tickDist, tickIndex++) {
        let tickLength = smallTickLength
        if(tickIndex % labelTickCount == 0)
          tickLength = labelTickLength
        
        if(tickIndex == 0)
          tickLength = labelTickLength

        let tickY = isTopRuler ? Math.floor(this.bedRegion.top + t + origin) : Math.floor(this.bedRegion.bottom - t - origin)

        if(origin + t >= 0 && origin + t <= verticalRulerDistance) {
          if(isLeftRuler) {
            ctx.moveTo(this.bedRegion.left - tickLength + 0.5, tickY+0.5)
            ctx.lineTo(this.bedRegion.left + 0.5, tickY+0.5)
          } else {
            ctx.moveTo(this.bedRegion.right + 0.5, tickY+0.5)
            ctx.lineTo(this.bedRegion.right + tickLength + 0.5, tickY+0.5)
          }
        }
      }

      ctx.stroke()

      ctx.fillStyle = this.rulerLabelColor

      // Horizontal - Labels
      tickIndex = 0
      origin = this.originOffsetX
      for(t=0; t<horizontalRulerDistance; t += tickDist, tickIndex++) {
        if(tickIndex % labelTickCount == 0) {
          let sizeLabel = ('' + Math.round(t / (this.scale * this.zoomScale)))
          let tickX = isLeftRuler ? Math.floor(this.bedRegion.left + t + origin) : Math.floor(this.bedRegion.right - t - origin)

          if(origin + t >= 0 && origin + t <= horizontalRulerDistance) {
            if(isTopRuler)
              ctx.fillText(sizeLabel, tickX+0.5 + 2, this.bedRegion.top - this.rulerSize / 2 + 8)
            else
              ctx.fillText(sizeLabel, tickX+0.5 + 2, this.bedRegion.bottom + this.rulerSize / 2 + 2)
          }
        }
      }

      // Vertical - Labels
      ctx.save()
      ctx.rotate(-Math.PI/2)
      ctx.textAlign = isTopRuler ? 'right' : 'left'
      origin = this.originOffsetY
      tickIndex = 0
      for(t=0; t<verticalRulerDistance; t += tickDist, tickIndex++) {
        if(tickIndex % labelTickCount == 0) {
          let sizeLabel = ('' + Math.round(t / (this.scale * this.zoomScale)))
          let tickY = isTopRuler ? Math.floor(this.bedRegion.top + t + origin) : Math.floor(this.bedRegion.bottom - t - origin)

          if(isLeftRuler)
            ctx.fillText(sizeLabel, -tickY+0.5 - 4, this.bedRegion.left - 2)
          else
            ctx.fillText(sizeLabel, -tickY+0.5 - (isTopRuler ? 4 : -2), this.bedRegion.right + 11)
        }
      }
      ctx.restore()
    },


    drawNegativeSpace(ctx, width, height) {
      let isLeftToRight = this.bedConfig.originHorizontal == AlignmentTypes.left
      let isTopToBottom = this.bedConfig.originVertical == AlignmentTypes.top

      // Fill in the area in negative space
      ctx.fillStyle = this.negativeSpaceColour
      ctx.beginPath()
      
      var size = Math.floor(this.negativeSpaceSize * this.scale * this.zoomScale)
      let horzLeft = this.bedRegion.left
      let horzTop = isTopToBottom ? (this.bedRegion.top + this.originOffsetY - size) : (this.bedRegion.bottom - this.originOffsetY)
      var horzWidth = this.bedRegion.right - this.bedRegion.left

      let vertLeft = isLeftToRight ? (this.bedRegion.left + this.originOffsetX - size) : (this.bedRegion.right - this.originOffsetX)
      let vertTop = this.bedRegion.top
      var horzHeight = this.bedRegion.bottom - this.bedRegion.top

      // Horizontal
      ctx.moveTo(horzLeft + 0.5, horzTop + 0.5)
      ctx.lineTo(horzLeft + horzWidth + 0.5, horzTop + 0.5)
      ctx.lineTo(horzLeft + horzWidth + 0.5, horzTop + size + 0.5)
      ctx.lineTo(horzLeft + 0.5, horzTop + size + 0.5)
      ctx.lineTo(horzLeft + 0.5, horzTop + 0.5)

      // Vertical
      ctx.moveTo(vertLeft + 0.5, vertTop + 0.5)
      ctx.lineTo(vertLeft + size + 0.5, vertTop + 0.5)
      ctx.lineTo(vertLeft + size + 0.5, vertTop + horzHeight + 0.5)
      ctx.lineTo(vertLeft + 0.5, vertTop + horzHeight + 0.5)
      ctx.lineTo(vertLeft + 0.5, vertTop + 0.5)

      ctx.fill()
    },

    drawGrid(ctx, width, height) {
      let gridSize = this.gridSize * this.scale

      let isLeftToRight = this.bedConfig.originHorizontal == AlignmentTypes.left
      let isTopToBottom = this.bedConfig.originVertical == AlignmentTypes.top

      var offsetX = Math.round(this.originOffsetX % gridSize)
      var offsetY = Math.round(-this.originOffsetY % gridSize)
      var buffer = gridSize*2

      let left = this.bedRegion.left - buffer
      let right = this.bedRegion.right + buffer
      let top = this.bedRegion.top - buffer
      let bottom = this.bedRegion.bottom + buffer

      // Grid
      ctx.strokeStyle = this.gridColour
      ctx.beginPath()
      if(isTopToBottom) {
        for(let y=top; y<=bottom; y+=gridSize) {
          ctx.moveTo(left + offsetX + 0.5, Math.floor(y + offsetY) + 0.5)
          ctx.lineTo(right + offsetX + 0.5, Math.floor(y + offsetY) + 0.5)
        }
      } else {
        for(let y=bottom; y>=top; y-=gridSize) {
          ctx.moveTo(left + offsetX + 0.5, Math.floor(y + offsetY) + 0.5)
          ctx.lineTo(right + offsetX + 0.5, Math.floor(y + offsetY) + 0.5)
        }
      }

      if(isLeftToRight) {
        for(let x=left; x<=right; x+=gridSize) {
          ctx.moveTo(Math.floor(x + offsetX) + 0.5, top + offsetY + 0.5)
          ctx.lineTo(Math.floor(x + offsetX) + 0.5, bottom + offsetY + 0.5)
        }
      } else {
        for(let x=right; x>=left; x-=gridSize) {
          ctx.moveTo(Math.floor(x + offsetX) + 0.5, top + offsetY + 0.5)
          ctx.lineTo(Math.floor(x + offsetX) + 0.5, bottom + offsetY + 0.5)
        }
      }
      ctx.stroke()      
    },

    drawOriginGuides(ctx, width, height) {
      let isLeftToRight = this.bedConfig.originHorizontal == AlignmentTypes.left
      let isTopToBottom = this.bedConfig.originVertical == AlignmentTypes.top

      var originGuideHeight = (this.bedConfig.originGuideHeight || 0) * this.scale * this.zoomScale
      var originGuideWidth = (this.bedConfig.originGuideWidth || 0) * this.scale * this.zoomScale

      if(originGuideHeight <= 0 && originGuideWidth <= 0)
        return

      this.bedBayList.forEach(bay => {
        var offsetX = Math.floor(bay.position * this.scale * this.zoomScale)
        offsetX = isLeftToRight ? (offsetX + this.bedRegion.left + this.originOffsetX) : (this.bedRegion.right - offsetX - this.originOffsetX)
        var offsetY = isTopToBottom ? (this.bedRegion.top + this.originOffsetY) : (this.bedRegion.bottom - this.originOffsetY)

        ctx.strokeStyle = this.originGuideColour
        ctx.beginPath()
        if(isTopToBottom) {
          ctx.moveTo(offsetX + 0.5, offsetY + 0.5)
          ctx.lineTo(offsetX + 0.5, offsetY + originGuideHeight + 0.5)
        } else {
          ctx.moveTo(offsetX + 0.5, offsetY + 0.5)
          ctx.lineTo(offsetX + 0.5, offsetY - originGuideHeight + 0.5)
        }
        if(isLeftToRight) {
          ctx.moveTo(offsetX + 0.5, offsetY + 0.5)
          ctx.lineTo(offsetX + originGuideWidth + 0.5, offsetY + 0.5)
        } else {
          ctx.moveTo(offsetX + 0.5, offsetY + 0.5)
          ctx.lineTo(offsetX - originGuideWidth + 0.5, offsetY + 0.5)
        }
        ctx.stroke()
      })
    },

    drawPositions(ctx, width, height) {
      let isLeftRuler = this.bedConfig.originHorizontal == AlignmentTypes.left

      this.bedBayList.forEach(bay => {
        if(Math.floor(bay.position) <= 0)
          return

        var offset = Math.floor(bay.position * this.scale * this.zoomScale)
        offset = isLeftRuler ? (offset + this.bedRegion.left + this.originOffsetX) : (this.bedRegion.right - offset - this.originOffsetX)

        ctx.strokeStyle = this.positionColour
        ctx.setLineDash([4, 4]);
        ctx.beginPath()
        ctx.moveTo(offset + 0.5, this.bedRegion.top + 0.5)
        ctx.lineTo(offset + 0.5, this.bedRegion.bottom + 0.5)
        ctx.stroke()
        ctx.setLineDash([]);
      })
    },

    drawProductionPanel(ctx, bay, width, height) {
      var displayOptions = {
        showCutoutOrder: this.cutoutOptionsAvailable
      }
      bay.panelObj.drawWaterjetDisplay(ctx, this.bedConfig, displayOptions, this.rulerSize, this.originOffsetX, this.originOffsetY, this.scale * this.zoomScale, this.scale * this.zoomScale, width, height)
    },

    getWaterjetBedCanvas() {
      return this.$refs.waterjetCanvas
    }
  }
  
}
</script>